import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  MapContainer,
  TileLayer,
  FeatureGroup,
  Polygon,
  Tooltip,
  useMap,
  Marker,
} from "react-leaflet";
import { EditControl } from "react-leaflet-draw";
import L, { LatLngTuple } from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import { fetchAdressefromOpenStreetMapApi } from "../../../api/projects.api";
import { FullscreenControl } from "react-leaflet-fullscreen";
import { MapCard } from "../AjouterProjetForm.styles";
import toast from "react-hot-toast";
import * as turf from "@turf/turf";
import { Button } from "antd";
import svg_icon from "../../../assets/icons8-location.gif";
import { useMediaQuery } from "react-responsive";

// Component to handle map updates
const MapUpdater = ({
  communeId,
  communesData,
  setCurrentCommuneLayer,
}: {
  communeId: number;
  communesData: any;
  setCurrentCommuneLayer: (layer: L.GeoJSON | null) => void;
}) => {
  const map = useMap();

  useEffect(() => {
    if (communeId && communesData?.features) {
      // Find the commune by OBJECTID
      const selectedCommune = communesData.features.find(
        (feature: any) => feature.properties.OBJECTID === communeId
      );

      if (selectedCommune) {
        // Remove any existing commune layers
        map.eachLayer((layer: any) => {
          if (layer.communeBoundary) {
            map.removeLayer(layer);
          }
        });

        // Create a GeoJSON layer for the commune boundary
        const communeLayer = L.geoJSON(selectedCommune, {
          style: {
            color: "#66008b",
            weight: 4,
            opacity: 1,
            fillOpacity: 0,
          },
        });

        // Add a custom property to identify this layer
        (communeLayer as any).communeBoundary = true;

        // Fit map to commune bounds
        const bounds = communeLayer.getBounds();
        map.fitBounds(bounds, { padding: [50, 50] });

        // Add the layer to the map and update the ref
        communeLayer.addTo(map);
        setCurrentCommuneLayer(communeLayer);
      }
    }
  }, [communeId, communesData, map, setCurrentCommuneLayer]);

  return null;
};

// Component to handle zooming to coordinates
const ZoomToCoordinates = ({ lat, lng, setTempMarker }: { lat: number; lng: number; setTempMarker: (coords: [number, number] | null) => void }) => {
  const map = useMap();

  useEffect(() => {
    if (!isNaN(lat) && !isNaN(lng)) {
      map.setView([lat, lng]);
      setTempMarker([lat, lng]);
    }
  }, [lat, lng, map, setTempMarker]);

  return null;
};

interface Tranche {
  id: number;
  name: string;
  surface_fonciere: number;
  surface_construite: number;
  surface_vendable: number;
}

interface MapComponentProps {
  tranches: Tranche[];
  formData: any;
  setFormData: (data: any) => void;
  selectedCommune?: number;
}

interface PolygonData {
  latitude: number;
  longitude: number;
  coordinates: [number, number][];
  adresse: string;
}

const TranchesLocationMap: React.FC<MapComponentProps> = ({
  tranches,
  formData,
  setFormData,
  selectedCommune,
}) => {
  const [polygons, setPolygons] = useState<PolygonData[]>(
    formData.adresses || []
  );
  const [communesData, setCommunesData] = useState<any>(null);
  const [currentCommuneLayer, setCurrentCommuneLayer] =
    useState<L.GeoJSON | null>(null);
  const [previousCommune, setPreviousCommune] = useState<number | undefined>(
    selectedCommune
  );
  const featureGroupRef = useRef<L.FeatureGroup>(null);
  const maxPolygons = tranches.length;

  // Add these state variables inside your TranchesLocationMap component
  const [latInput, setLatInput] = useState<string>("");
  const [lngInput, setLngInput] = useState<string>("");
  const [tempMarker, setTempMarker] = useState<[number, number] | null>(null);


  // for responsive
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 575px)" });

  // Load communes data
  useEffect(() => {
    import("../../../data/communes_geo_data.min.json").then((data) => {
      setCommunesData(data);
    });
  }, []);

  // Clear polygons when commune changes
  useEffect(() => {
    if (selectedCommune !== previousCommune) {
      setPolygons([]);
      setFormData({ ...formData, adresses: [] });

      // Clear all layers from the FeatureGroup
      if (featureGroupRef.current) {
        featureGroupRef.current.clearLayers();
      }

      setPreviousCommune(selectedCommune);
    }
  }, [selectedCommune, previousCommune, setFormData, formData]);

  useEffect(() => {
    if (polygons.length > maxPolygons) {
      setPolygons(polygons.slice(0, maxPolygons));
    }
  }, [maxPolygons, polygons]);

  useEffect(() => {
    setFormData({ ...formData, adresses: polygons });
  }, [polygons]);

  const isPolygonIntersectingCommune = (layer: L.Polygon): boolean => {
    if (!currentCommuneLayer) return false;
    try {
      // Get the FeatureCollection from the communeLayer
      const communeGeoJSON: any = currentCommuneLayer.toGeoJSON();
      // Extract the actual Feature from the FeatureCollection
      const communeFeature = communeGeoJSON.features[0];
      // If we can't get a feature, fallback to false
      if (!communeFeature) {
        return false;
      }
      // Get GeoJSON from the drawn polygon
      const polygonGeoJSON = layer.toGeoJSON();
      
      // Use turf.js to check if polygon intersects with commune
      // booleanIntersects checks if the geometries have any points in common
      return turf.booleanIntersects(polygonGeoJSON, communeFeature);
    } catch (error) {
      console.error("Error checking polygon intersection:", error);
      return false;
    }
  };

  const onCreated = async (e: L.DrawEvents.Created) => {
    const layer = e.layer as L.Polygon;

    // Remove temporary marker when polygon is drawn
    setTempMarker(null);

    if (polygons.length >= maxPolygons) {
      featureGroupRef.current?.removeLayer(layer);
      toast.error(
        `Vous ne pouvez dessiner que jusqu'à ${maxPolygons} polygones.`
      );
      return;
    }

    if (!isPolygonIntersectingCommune(layer)) {
      featureGroupRef.current?.removeLayer(layer);
      toast.error(
        "Le polygone doit avoir au moins un point à l'intérieur des limites de la commune."
      );
      return;
    }

    try {
      // Récupérer les coordonnées au format [lat, lng]
      const latLngs = layer.getLatLngs()[0] as L.LatLng[];
      const polygonCoords: [number, number][] = latLngs.map((latlng) => [
        latlng.lat,
        latlng.lng,
      ]);

      // Pour Turf, inverser en [lng, lat]
      let polygonCoordsTurf: [number, number][] = latLngs.map((latlng) => [
        latlng.lng,
        latlng.lat,
      ]);

      // Vérifier si le polygone est fermé, sinon le fermer
      const firstCoord = polygonCoordsTurf[0];
      const lastCoord = polygonCoordsTurf[polygonCoordsTurf.length - 1];
      if (firstCoord[0] !== lastCoord[0] || firstCoord[1] !== lastCoord[1]) {
        polygonCoordsTurf.push(firstCoord);
      }

      // Calculer l'aire du polygone en m²
      const areaCalculated = turf.area(turf.polygon([polygonCoordsTurf]));

      const expectedSurfaceFonciere =
        tranches[polygons.length]?.surface_fonciere;
      if (expectedSurfaceFonciere) {
        const tolerance = expectedSurfaceFonciere * 0.5;
        if (Math.abs(areaCalculated - expectedSurfaceFonciere) > tolerance) {
          toast.error(
            `La surface du polygone (${areaCalculated.toFixed(
              2
            )} m²) ne correspond pas à la surface foncière saisie (${expectedSurfaceFonciere} m²).`
          );
          featureGroupRef.current?.removeLayer(layer);
          return;
        }
      }

      const center = layer.getBounds().getCenter();
      const addressData = await fetchAdressefromOpenStreetMapApi(
        center.lat,
        center.lng
      );

      const newPolygon: PolygonData = {
        latitude: center.lat,
        longitude: center.lng,
        coordinates: polygonCoords,
        adresse: addressData.display_name,
      };

      setPolygons((prev) => [...prev, newPolygon]);
      setFormData({
        ...formData,
        adresses: [...(formData.adresses || []), newPolygon],
      });
    } catch (error) {
      console.error("Error processing polygon:", error);
      featureGroupRef.current?.removeLayer(layer);
    }
  };

  const onDeleted = (e: L.DrawEvents.Deleted) => {
    e.layers.eachLayer((layer) => {
      if (layer instanceof L.Polygon) {
        const latLngs = layer.getLatLngs()[0] as L.LatLng[];
        const deletedCoordinates: [number, number][] = latLngs.map((latlng) => [
          latlng.lat,
          latlng.lng,
        ]);
        setPolygons((prev) =>
          prev.filter(
            (poly) =>
              JSON.stringify(poly.coordinates) !==
              JSON.stringify(deletedCoordinates)
          )
        );
      }
    });
  };
  const stadiaApiKey = "0e49e10c-116e-435d-a2ee-437f7190699e";
  

  return (
    <MapCard>
      <div style={{ marginBottom: "10px", display: "flex", gap: "10px",flexDirection:isTabletOrMobile?'column':'row' }}>
        <input
          type="text"
          placeholder="Latitude"
          value={latInput}
          onChange={(e) => setLatInput(e.target.value)}
          style={{
            padding: "8px",
            borderRadius: "4px",
            border: "1px solid #ccc",
          }}
        />
        <input
          type="text"
          placeholder="Longitude"
          value={lngInput}
          onChange={(e) => setLngInput(e.target.value)}
          style={{
            padding: "8px",
            borderRadius: "4px",
            border: "1px solid #ccc",
          }}
        />
        <Button
          type="primary"
          onClick={() => {
            const lat = parseFloat(latInput);
            const lng = parseFloat(lngInput);
            if (!isNaN(lat) && !isNaN(lng)) {
              setTempMarker([lat, lng]);
            }
          }}
        >
          Zoomer
        </Button>
      </div>
      <MapContainer
        center={[33.5731, -7.5898]}
        zoom={13}
        style={{ height: "350px", width: "100%", borderRadius: "10px" }}
      >
        {/* Base satellite layer */}
        <TileLayer
          attribution="Google Maps"
          url="https://www.google.cn/maps/vt?lyrs=s@189&gl=cn&x={x}&y={y}&z={z}"
        />
        {/* Transparent roads-only layer */}
        <TileLayer
          attribution="OpenStreetMap/Carto"
          url="https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png"
        />
        <FullscreenControl position="topright" />
        <MapUpdater
          communeId={selectedCommune ?? 0}
          communesData={communesData}
          setCurrentCommuneLayer={setCurrentCommuneLayer}
        />
        {tempMarker && (
          <ZoomToCoordinates lat={tempMarker[0]} lng={tempMarker[1]} setTempMarker={setTempMarker} />
        )}
        {tempMarker && (
          <Marker  icon={
            new L.Icon({
              iconUrl: svg_icon,
              iconSize: [32, 32],
              iconAnchor: [16, 32],
            })
          } position={tempMarker}>
           
          </Marker>
        )}
        <FeatureGroup ref={featureGroupRef}>
          <EditControl
            position="topright"
            onCreated={onCreated}
            onDeleted={onDeleted}
            draw={{
              rectangle: false,
              circle: false,
              marker: false,
              circlemarker: false,
              polyline: false,
              polygon: !!selectedCommune && polygons.length < maxPolygons,
            }}
          />
          {polygons.map((polygon, index) => (
            <Polygon
              fillColor="red"
              key={index}
              positions={polygon.coordinates}
            >
              <Tooltip direction="top" permanent={false} sticky={true}>
                <div style={{ textAlign: "center" }}>
                  <h4>{tranches[index]?.name || formData.nom_du_projet}</h4>
                  <p>
                    Surface Foncière: {tranches[index]?.surface_fonciere} m²
                  </p>
                  <p>
                    Surface Construite: {tranches[index]?.surface_construite} m²
                  </p>
                </div>
              </Tooltip>
            </Polygon>
          ))}
        </FeatureGroup>
      </MapContainer>
    </MapCard>
  );
};

export default TranchesLocationMap;